import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const DEMO = {
  admin:      { email:'admin@paperless.com',      password:'admin123',      label:'Admin',       color:'bg-indigo-600' },
  accountant: { email:'accountant@paperless.com', password:'accountant123',    label:'Accountant',  color:'bg-green-600' },
  employee:   { email:'employee@paperless.com',   password:'employee123',   label:'Employee',    color:'bg-blue-600' },
  contractor: { email:'contractor@paperless.com', password:'contractor123', label:'Contractor',  color:'bg-amber-600' },
};

export function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const submit = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await login(email, password);
      const dest = user.role === 'contractor' ? '/contractor' : '/' + user.role;
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  const fillDemo = role => {
    setEmail(DEMO[role].email);
    setPassword(DEMO[role].password);
    setError('');
  };

return (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
    <div className="w-full max-w-md">
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
        
        {/* Logo inside box */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Paperless MS Logo"
            className="w-48 h-20 object-contain mx-auto mb-4"
          />
          <p className="text-gray-500 text-sm mt-1">Sign in to your account</p>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-medium">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="you@company.com"
              autoComplete="email"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 focus:bg-white transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 focus:bg-white transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPw(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              >
                {showPw ? (
                  <EyeSlashIcon className="w-4 h-4" />
                ) : (
                  <EyeIcon className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm text-sm mt-2"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Demo accounts */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center mb-3">
            Quick Demo Login
          </p>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(DEMO).map(([role, d]) => (
              <button
                key={role}
                type="button"
                onClick={() => fillDemo(role)}
                className={`${d.color} text-white text-xs font-semibold py-2.5 px-3 rounded-xl hover:opacity-90 transition-opacity shadow-sm`}
              >
                {d.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">
            Click a role to auto-fill credentials
          </p>
        </div>
      </div>
    </div>
  </div>
);
}